import { useCallback, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import ModalContext from '~/context/vdp/ModalContext';
import {
  type FavoritesArguments,
  type OnContinue
} from '~/hooks/common/favorites';
import { getIsReservableVehicle, getVehicleId } from '~/selectors';
import useAddFavorite from './useAddFavorite';
import useLogInAndFavorite from './useLogInAndFavorite';

function useMaybeOpenReservableNotificationModal() {
  const {
    isReservableNotificationModalOpen,
    setReservableNotificationModalOpen
  } = useContext(ModalContext);
  const currentVehicleId = useSelector(getVehicleId);
  const isReservable = useSelector(getIsReservableVehicle);

  return useCallback(
    (args: FavoritesArguments) => {
      if (
        args.vehicleId === currentVehicleId &&
        isReservable &&
        !isReservableNotificationModalOpen
      ) {
        setReservableNotificationModalOpen(true);
      }
    },
    [
      currentVehicleId,
      isReservable,
      isReservableNotificationModalOpen,
      setReservableNotificationModalOpen
    ]
  );
}

export default function useAddFavoriteFlow(
  args: { onAddFavorite?: OnContinue } = {}
) {
  const addFavoriteVehicle = useAddFavorite();
  const loginAndAddFavorite = useLogInAndFavorite();
  const maybeOpenReservableNotificationModal =
    useMaybeOpenReservableNotificationModal();
  const onAddFavorite =
    args.onAddFavorite ?? maybeOpenReservableNotificationModal;

  return useMemo(
    () => loginAndAddFavorite(addFavoriteVehicle(onAddFavorite)),
    [addFavoriteVehicle, loginAndAddFavorite, onAddFavorite]
  );
}
